"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Animation = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const react_transition_group_1 = require("react-transition-group");
const styles = (timeoutVal) => (0, app_1.css) `
  .fade-${timeoutVal}-enter {
    opacity: 0;
    transform: translateY(-10px);
  }
  .fade-${timeoutVal}-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity ${timeoutVal}ms ease, transform ${timeoutVal}ms ease;
  }
  .fade-${timeoutVal}-exit {
    opacity: 1;
    transform: translateY(0);
  }
  .fade-${timeoutVal}-exit-active {
    opacity: 0;
    transform: translateY(-10px);
    transition: opacity ${timeoutVal}ms ease, transform ${timeoutVal}ms ease;
  }
  .opacity-${timeoutVal}-enter, .opacity-${timeoutVal}-appear {
    opacity: 0;
  }
  .opacity-${timeoutVal}-enter-active {
    opacity: 1;
    transition: opacity ${timeoutVal}ms ease;
  }
  .opacity-${timeoutVal}-exit {
    opacity: 1;
  }
  .opacity-${timeoutVal}-exit-active {
    opacity: 0;
    transition: opacity ${timeoutVal}ms ease;
  }
`;
function getCombinedAutoTransition(el, property, timeout) {
    const { transitionProperty, transitionDuration, transitionTimingFunction } = window.getComputedStyle(el);
    const transition = [
        transitionProperty,
        transitionDuration,
        transitionTimingFunction,
    ].join(' ');
    return `${transition}, ${property} ${timeout}ms ease, margin ${timeout}ms ease, padding ${timeout}ms ease`;
}
const setupAutoTransition = (timeout, setTimeoutVal, auto) => {
    if (!auto) {
        return {};
    }
    const margin = auto === 'width'
        ? ['marginRight', 'marginLeft']
        : ['marginTop', 'marginBottom'];
    const padding = auto === 'width'
        ? ['paddingRight', 'paddingLeft']
        : ['paddingTop', 'paddingBottom'];
    const sizes = [...margin, ...padding];
    return {
        onEnter: (element) => {
            const el = element;
            if (!element.getAttribute(`data-${auto}`)) {
                el.style[auto] = 'unset';
            }
            const activeAnimation = el.style.transition !== '';
            const currentOffset = el[`offset${(0, utils_1.capitalize)(auto)}`];
            if (activeAnimation) {
                el.style[auto] = `${currentOffset}px`;
            }
            else {
                el.setAttribute(`data-${auto}`, currentOffset);
                el.style[auto] = '0';
                sizes.forEach((m) => {
                    el.setAttribute(`data-${m}`, Number.parseInt(window.getComputedStyle(el)[m], 10));
                    el.style[m] = '0';
                });
            }
        },
        onEntering: (element) => {
            const el = element;
            const currentOffset = el[`offset${(0, utils_1.capitalize)(auto)}`];
            const activeAnimation = el.style.transition !== '';
            if (activeAnimation) {
                el.style.transition = '';
                const animationProgress = Number(currentOffset) / Number(el.getAttribute(`data-${auto}`));
                const timeoutLeft = Math.round(timeout - timeout * animationProgress);
                setTimeoutVal(timeoutLeft);
                el.style.transition = getCombinedAutoTransition(el, auto, timeoutLeft);
            }
            else {
                el.style.transition = getCombinedAutoTransition(el, auto, timeout);
            }
            el.style.overflow = 'hidden';
            el.style[auto] = `${el.getAttribute(`data-${auto}`)}px`;
            sizes.forEach((m) => {
                el.style[m] = `${el.getAttribute(`data-${m}`)}px`;
                el.removeAttribute(`data-${m}`);
            });
        },
        onEntered: (element) => {
            const el = element;
            el.style[auto] = '';
            el.style.transition = '';
            setTimeoutVal(timeout);
            sizes.forEach((m) => {
                el.style[m] = '';
            });
        },
        onExit: (element) => {
            const el = element;
            el.style[auto] = `${el[`offset${(0, utils_1.capitalize)(auto)}`]}px`;
            sizes.forEach((m) => {
                el.style[m] = Number.parseInt(window.getComputedStyle(el)[m], 10);
            });
        },
        onExiting: (element) => {
            const el = element;
            const activeAnimation = el.style.transition !== '';
            const currentOffset = el[`offset${(0, utils_1.capitalize)(auto)}`];
            el.style.transition = '';
            if (activeAnimation) {
                const currentAnimationPercentage = Number(currentOffset) / Number(el.getAttribute(`data-${auto}`));
                const timeoutLeft = Math.round(timeout * currentAnimationPercentage);
                el.style.transition = getCombinedAutoTransition(el, auto, timeoutLeft);
            }
            else {
                el.style.transition = getCombinedAutoTransition(el, auto, timeout);
            }
            el.style[auto] = '0';
            sizes.forEach((m) => {
                el.style[m] = '0';
            });
        },
        onExited: () => {
            setTimeoutVal(timeout);
        },
    };
};
const KEY_FALLBACK = 'animation-elem';
function Animation({ children, timeout = 200, type = 'opacity', auto, in: in_, }) {
    const singleRef = (0, react_1.useRef)();
    const [timeoutVal, setTimeoutVal] = (0, react_1.useState)(timeout);
    const globalStyles = (0, react_1.useMemo)(() => styles(timeout), [timeout]);
    const isSingleChild = (0, react_1.useMemo)(() => in_ !== undefined, [in_]);
    (0, react_1.useEffect)(() => {
        const node = singleRef.current;
        if (isSingleChild && auto && node && !node.getAttribute(`data-${auto}`)) {
            node.style[auto] = '0';
        }
    }, [singleRef, auto, isSingleChild]);
    const content = (0, react_1.useMemo)(() => {
        var _a;
        if (isSingleChild && (0, react_1.isValidElement)(children)) {
            return ((0, jsx_runtime_1.jsx)(react_transition_group_1.CSSTransition, Object.assign({ timeout: timeoutVal, in: Boolean(in_), classNames: `${type}-${timeoutVal}` }, setupAutoTransition(timeout, setTimeoutVal, auto), { children: (0, react_1.cloneElement)(children, { ref: singleRef }) }), ((_a = children.props) === null || _a === void 0 ? void 0 : _a.id) || KEY_FALLBACK));
        }
        return ((0, jsx_runtime_1.jsx)(react_transition_group_1.TransitionGroup, Object.assign({ component: null }, { children: react_1.Children.map(children, (child) => {
                var _a, _b;
                return (0, react_1.isValidElement)(child) && ((0, jsx_runtime_1.jsx)(react_transition_group_1.CSSTransition, Object.assign({ timeout: timeoutVal, classNames: `${type}-${timeoutVal}` }, setupAutoTransition(timeout, setTimeoutVal, auto), { children: child }), (_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : KEY_FALLBACK));
            }) })));
    }, [auto, children, in_, isSingleChild, timeout, timeoutVal, type]);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [content, (0, jsx_runtime_1.jsx)(app_1.Global, { styles: globalStyles })] }));
}
exports.Animation = Animation;
